import React from 'react';
import { Button } from '../components/ui/button';
import { AlertCircle } from 'lucide-react';
import ContactButton from '../components/ContactButton';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-cream">
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <Button onClick={() => window.location.href = '/'} variant="ghost" className="text-cream">Back to Home</Button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-6 pt-32 pb-16 max-w-4xl">
        <h1 className="text-5xl font-black text-charcoal uppercase mb-8">Refund & Return Policy</h1>
        <p className="text-warmGray mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

        <div className="bg-cream border-2 border-warmSage p-6 rounded-lg mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-charcoal flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold text-charcoal mb-2">Important Notice</h3>
              <p className="text-charcoal">Custom and personalized orders are final sale. Please review all details carefully before approval.</p>
            </div>
          </div>
        </div>

        <div className="space-y-8 text-charcoal">
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Custom Orders - No Refunds</h2>
            <p className="text-warmGray leading-relaxed mb-4"><strong>All custom uniform orders are final sale and non-refundable once production begins.</strong></p>
            <p className="text-warmGray leading-relaxed mb-4">Custom orders include any products with:</p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li>Personalized player names or numbers</li>
              <li>Custom team logos or designs</li>
              <li>Specific size combinations for teams</li>
              <li>Custom color combinations or modifications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Customer Responsibility</h2>
            <p className="text-warmGray leading-relaxed mb-4">It is the customer's responsibility to verify all information is correct before final approval, including:</p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li>Spelling of all player names</li>
              <li>Player numbers</li>
              <li>Size selections (Adult S-3XL, Youth YS-Y2XL)</li>
              <li>Logo files (quality, format, colors)</li>
              <li>Design specifications and placement</li>
            </ul>
            <p className="text-warmGray leading-relaxed mt-4"><strong>PRSVR is not responsible for errors in customer-provided information.</strong></p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Manufacturing Defects Only</h2>
            <p className="text-warmGray leading-relaxed mb-4">We will replace or repair uniforms ONLY in cases of verified manufacturing defects or errors made by PRSVR during production.</p>
            <p className="text-warmGray leading-relaxed mb-4"><strong>Manufacturing defects do NOT include:</strong></p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li>Customer ordering errors (wrong names, numbers, sizes)</li>
              <li>Size dissatisfaction or fit preferences</li>
              <li>Design preferences or style choices</li>
              <li>Low-quality customer-provided logos</li>
              <li>Color variations due to screen/monitor differences</li>
              <li>Normal wear and tear after delivery</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Restocking Fee</h2>
            <p className="text-warmGray leading-relaxed mb-4">In the rare cases where PRSVR agrees to accept a return due to a manufacturing defect verified by PRSVR:</p>
            <p className="text-warmGray leading-relaxed mb-4 font-bold">A 25% restocking fee will be applied to all approved returns.</p>
            <p className="text-warmGray leading-relaxed">This fee covers inspection, processing, and handling costs. Shipping costs are non-refundable.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Order Review Period</h2>
            <p className="text-warmGray leading-relaxed mb-4">Customers will receive an order confirmation with all details for review before production begins.</p>
            <p className="text-warmGray leading-relaxed mb-4 font-bold">You have 48 hours from receiving the confirmation to review and approve or request changes.</p>
            <p className="text-warmGray leading-relaxed">Once you approve the order or the 48-hour review period expires, production begins and the order becomes final.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Standard (Non-Custom) Products</h2>
            <p className="text-warmGray leading-relaxed mb-4">Standard, non-customized items from our ready-to-wear collection may be eligible for return within 14 days of delivery if:</p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li>Item is unworn, unwashed, and in original condition</li>
              <li>All original tags are attached</li>
              <li>Item is in original packaging</li>
            </ul>
            <p className="text-warmGray leading-relaxed mt-4">Contact us at info@prsvr.com within 14 days of delivery to initiate a return. A 25% restocking fee applies. Return shipping costs are the customer's responsibility.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Defective Products</h2>
            <p className="text-warmGray leading-relaxed mb-4">If you receive a defective product due to a manufacturing error by PRSVR:</p>
            <ol className="list-decimal list-inside text-warmGray space-y-2 ml-4">
              <li>Contact us immediately at info@prsvr.com or (661) 553-2186</li>
              <li>Provide photos of the defect</li>
              <li>Include your order number and description of the issue</li>
            </ol>
            <p className="text-warmGray leading-relaxed mt-4">We will review and, if confirmed as our error, provide a replacement at no charge or offer a refund minus the 25% restocking fee.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Exchanges</h2>
            <p className="text-warmGray leading-relaxed mb-4">We do not offer exchanges on custom orders. For standard items, exchanges for different sizes may be available within 14 days if the item meets return criteria.</p>
            <p className="text-warmGray leading-relaxed">Contact us to arrange an exchange. You are responsible for return shipping, and a 25% restocking fee applies.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Refund Processing</h2>
            <p className="text-warmGray leading-relaxed mb-4">Approved refunds will be processed within 7-10 business days after we receive and inspect the returned item.</p>
            <p className="text-warmGray leading-relaxed">Refunds will be issued to the original payment method. Please allow 5-10 business days for the refund to appear in your account.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Samples</h2>
            <p className="text-warmGray leading-relaxed mb-4">We offer samples for $200 per single uniform. Samples are non-refundable but can be applied as credit toward a team order of 8 or more uniforms placed within 30 days.</p>
            <p className="text-warmGray leading-relaxed">Contact info@prsvr.com to request a sample.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. Questions?</h2>
            <p className="text-warmGray leading-relaxed mb-4">If you have questions about our refund policy or need assistance with an order:</p>
            <div className="bg-charcoal text-cream p-6 rounded-lg">
              <p className="font-bold mb-2">Contact PRSVR</p>
              <p>Email: info@prsvr.com</p>
              <p>Phone: (661) 553-2186</p>
              <p className="mt-4">We're here to help ensure you're satisfied with your PRSVR experience!</p>
            </div>
          </section>
        </div>
      </div>
      <ContactButton />
    </div>
  );
};

export default RefundPolicy;